import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { MovaraMark } from "@/components/Brand";
import client, { formatApiErrorDetail } from "@/lib/api";

export default function AcceptInvite() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError("This invitation link is invalid.");
      setLoading(false);
      return;
    }
    client
      .get(`/invitations/${token}`)
      .then((r) => setInvite(r.data))
      .catch((err) => setLoadError(formatApiErrorDetail(err.response?.data?.detail) || "This invitation link is invalid."))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      const r = await client.post(`/invitations/${token}/accept`, { name: name.trim(), password });
      setSession(r.data.user, r.data.token);
      navigate("/app/today");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not accept invitation.");
    } finally {
      setBusy(false);
    }
  };

  const roleLabel = invite?.role === "admin" ? "Workspace Admin" : (invite?.role ? invite.role.charAt(0).toUpperCase() + invite.role.slice(1) : "team member");

  return (
    <div className="min-h-screen bg-app-bg flex">
      <div className="hidden lg:flex w-1/2 bg-sidebar text-white flex-col justify-between p-12">
        <Link to="/" className="flex items-center gap-2.5 text-white">
          <MovaraMark className="h-7 w-8" />
          <span className="font-heading font-semibold text-lg">MovaRaBoard</span>
        </Link>
        <div className="max-w-md">
          <p className="op-label text-warn mb-3">You're invited</p>
          <h2 className="font-heading text-3xl font-medium leading-tight mb-4">
            Set up your account and join your team.
          </h2>
          <p className="text-white/60 text-[15px] leading-relaxed">
            One clear board for shipment jobs, delays, missing documents, customer updates,
            and team handoffs.
          </p>
        </div>
        <div className="text-white/40 text-xs font-mono-data">© 2026 MovaRaBoard · Shipment Visibility Platform</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8 text-brand">
            <MovaraMark className="h-7 w-8" />
            <span className="font-heading font-semibold text-lg text-ink">MovaRaBoard</span>
          </div>

          {loading && <p data-testid="invite-loading" className="text-sm text-ink-2">Checking your invitation…</p>}

          {!loading && loadError && (
            <div data-testid="invite-error-state">
              <div className="flex items-center gap-2 text-danger mb-2">
                <AlertTriangle className="h-5 w-5" />
                <h1 className="font-heading text-xl font-semibold">Invitation unavailable</h1>
              </div>
              <p className="text-sm text-ink-2 mb-6">{loadError}</p>
              <Link to="/login" className="text-brand font-medium text-sm hover:underline">Go to sign in →</Link>
            </div>
          )}

          {!loading && invite?.status === "accepted" && (
            <div data-testid="invite-accepted-state">
              <div className="flex items-center gap-2 text-ok mb-2">
                <CheckCircle2 className="h-5 w-5" />
                <h1 className="font-heading text-xl font-semibold text-ink">Invitation already used</h1>
              </div>
              <p className="text-sm text-ink-2 mb-6">This invite has already been accepted. Please sign in with your account.</p>
              <Link to="/login" data-testid="invite-signin-link" className="text-brand font-medium text-sm hover:underline">Go to sign in →</Link>
            </div>
          )}

          {!loading && invite?.status === "pending" && (
            <>
              <h1 className="font-heading text-2xl font-semibold text-ink mb-1">Join {invite.company_name}</h1>
              <p className="text-sm text-ink-2 mb-6">
                You've been invited as <span className="font-medium text-ink">{roleLabel}</span>. Set your password to get started.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="op-label block mb-1.5">Work email</label>
                  <input data-testid="invite-email" type="email" value={invite.email} disabled
                    className="w-full h-10 px-3 rounded-sm border border-line bg-surface-alt text-sm text-ink-2 font-mono-data" />
                </div>
                <div>
                  <label className="op-label block mb-1.5">Full name</label>
                  <input data-testid="invite-name-input" value={name} onChange={(e) => setName(e.target.value)} required autoFocus
                    className="w-full h-10 px-3 rounded-sm border border-line bg-surface text-sm text-ink focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand" />
                </div>
                <div>
                  <label className="op-label block mb-1.5">Create password</label>
                  <input data-testid="invite-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                    placeholder="At least 8 characters"
                    className="w-full h-10 px-3 rounded-sm border border-line bg-surface text-sm text-ink focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand" />
                </div>
                {error && <div data-testid="invite-submit-error" className="text-[13px] text-danger bg-danger/[0.08] border border-danger/30 rounded-sm px-3 py-2">{error}</div>}
                <button data-testid="invite-submit-btn" type="submit" disabled={busy}
                  className="w-full h-10 rounded-sm bg-brand text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-brand-dark transition-colors disabled:opacity-60">
                  {busy ? "Setting up…" : "Accept invite & continue"}
                  {!busy && <ArrowRight className="h-4 w-4" />}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
